import React, {useEffect, useMemo, useRef, useState} from 'react'
import moment from 'moment'
import {KTSVG} from '../../../../../_metronic/helpers'
import LazySelect from '../../../../components/LazySelect'
import type {DashboardFilterValue, FilterFieldConfig, FilterOption} from './types'
import './DashboardFilterBar.scss'

type Props = {
  fields: FilterFieldConfig[]
  defaultStartDate: string
  defaultEndDate: string
  isLoading: boolean
  onApply: (value: DashboardFilterValue) => void
  onStagedSelectionsChange?: (selections: Record<string, string[]>) => void
}

// App filter-border token (matches every other filter's `#e4e6ef` = $gray-300).
const BORDER = 'var(--kt-gray-300, #e4e6ef)'

const selectStyles = {
  control: (base: any) => ({
    ...base,
    minWidth: 165,
    minHeight: 38,
    backgroundColor: '#fff',
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    boxShadow: 'none',
    ':hover': {borderColor: BORDER},
  }),
  placeholder: (base: any, state: any) => ({
    ...base,
    color: '#171a1f',
    fontSize: 14,
    fontWeight: state.isFocused ? 400 : 500,
    whiteSpace: 'nowrap',
  }),
  valueContainer: (base: any) => ({...base, paddingLeft: 28}), // room for the leading icon
  indicatorSeparator: () => ({display: 'none'}),
  dropdownIndicator: () => ({display: 'none'}),
  menu: (base: any) => ({...base, zIndex: 30, minWidth: 220}),
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  )
  useEffect(() => {
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(mql.matches)
    setMatches(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [query])
  return matches
}

// White pill select with a leading icon. Uses the shared LazySelect (app-standard
// in-dropdown search + lazy-load); the icon lives in a local wrapper so LazySelect
// itself stays untouched.
const IconSelect: React.FC<{
  iconPath: string
  options: FilterOption[]
  value: FilterOption[]
  onChange: (v: FilterOption[]) => void
  placeholder: string
  isLoading?: boolean
}> = ({iconPath, options, value, onChange, placeholder, isLoading}) => (
  <div className='dash-filter-select'>
    <KTSVG path={iconPath} className='dash-filter-select__icon' svgClassName='' />
    <LazySelect
      className=''
      classNamePrefix='react-select'
      options={options}
      value={value}
      onChange={(v: any) => onChange((v as FilterOption[]) ?? [])}
      placeholder={placeholder}
      isMulti
      isLoading={isLoading}
      styles={selectStyles}
    />
  </div>
)

const DateRangeFields: React.FC<{
  startDate: string
  endDate: string
  onChange: (start: string, end: string) => void
}> = ({startDate, endDate, onChange}) => (
  <div className='dash-filter-bar__dates'>
    <label className='dash-filter-pill__field'>
      <span>From</span>
      <input
        type='date'
        value={startDate}
        max={endDate || undefined}
        onChange={(e) => onChange(e.target.value, endDate)}
      />
    </label>
    <label className='dash-filter-pill__field'>
      <span>To</span>
      <input
        type='date'
        value={endDate}
        min={startDate || undefined}
        onChange={(e) => onChange(startDate, e.target.value)}
      />
    </label>
  </div>
)

const DateRangePill: React.FC<{
  startDate: string
  endDate: string
  onChange: (start: string, end: string) => void
  onReset: () => void
  canReset: boolean
}> = ({startDate, endDate, onChange, onReset, canReset}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const fmt = (d: string) => {
    const m = moment(d, 'YYYY-MM-DD', true)
    return m.isValid() ? m.format('MMM DD, YYYY') : d
  }
  const label =
    startDate && endDate
      ? `${fmt(startDate)}  →  ${fmt(endDate)}`
      : startDate
      ? fmt(startDate)
      : endDate
      ? fmt(endDate)
      : 'Date Range'

  return (
    <div className='dash-filter-pill' ref={ref}>
      <button type='button' className='dash-filter-pill__btn' onClick={() => setOpen((o) => !o)}>
        <KTSVG path='/media/icons/ipivis/clock.svg' className='dash-filter-pill__icon' svgClassName='' />
        <span>{label}</span>
      </button>
      {open && (
        <div className='dash-filter-pill__popover'>
          <label className='dash-filter-pill__field'>
            <span>From</span>
            <input
              type='date'
              value={startDate}
              max={endDate || undefined}
              onChange={(e) => onChange(e.target.value, endDate)}
            />
          </label>
          <label className='dash-filter-pill__field'>
            <span>To</span>
            <input
              type='date'
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => onChange(startDate, e.target.value)}
            />
          </label>
          {canReset && (
            <button type='button' className='dash-filter-pill__clear' onClick={onReset}>
              Reset
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export const DashboardFilterBar: React.FC<Props> = ({
  fields,
  defaultStartDate,
  defaultEndDate,
  isLoading,
  onApply,
  onStagedSelectionsChange,
}) => {
  const [startDate, setStartDate] = useState(defaultStartDate)
  const [endDate, setEndDate] = useState(defaultEndDate)
  const [selections, setSelections] = useState<Record<string, FilterOption[]>>({})

  const visibleFields = useMemo(() => fields.filter((f) => !f.hidden), [fields])

  const stagedRef = useRef(onStagedSelectionsChange)
  stagedRef.current = onStagedSelectionsChange
  useEffect(() => {
    stagedRef.current?.(
      Object.fromEntries(Object.entries(selections).map(([id, v]) => [id, v.map((o) => o.value)]))
    )
  }, [selections])

  useEffect(() => {
    setSelections((prev) => {
      let changed = false
      const next: Record<string, FilterOption[]> = {}
      for (const field of fields) {
        const picked = prev[field.id]
        if (!picked?.length) continue
        if (field.hidden) {
          changed = true
          continue
        }
        if (field.isLoading || field.options.length === 0) {
          next[field.id] = picked
          continue
        }
        const allowed = new Set(field.options.map((o) => o.value))
        const kept = picked.filter((o) => allowed.has(o.value))
        if (kept.length !== picked.length) changed = true
        if (kept.length) next[field.id] = kept
      }
      return changed ? next : prev
    })
  }, [fields])

  const isCompact = useMediaQuery('(max-width: 991px)')
  const [panelOpen, setPanelOpen] = useState(false)
  const compactRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!panelOpen) return
    const onDocClick = (e: MouseEvent) => {
      if (compactRef.current && !compactRef.current.contains(e.target as Node)) setPanelOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPanelOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [panelOpen])

  const isDateRangeDirty = startDate !== defaultStartDate || endDate !== defaultEndDate

  const activeCount =
    (isDateRangeDirty ? 1 : 0) +
    visibleFields.reduce((n, f) => n + (selections[f.id]?.length ? 1 : 0), 0)

  const setDates = (s: string, e: string) => {
    setStartDate(s)
    setEndDate(e)
  }

  const setFieldValue = (id: string, v: FilterOption[]) =>
    setSelections((prev) => ({...prev, [id]: v}))

  const handleApply = () => {
    const payload: Record<string, string[]> = {}
    for (const field of visibleFields) {
      payload[field.id] = (selections[field.id] ?? []).map((o) => o.value)
    }
    onApply({
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      selections: payload,
    })
    setPanelOpen(false)
  }

  const handleReset = () => {
    setStartDate(defaultStartDate)
    setEndDate(defaultEndDate)
    setSelections({})
  }

  const selects = (
    <>
      {visibleFields.map((field) => (
        <IconSelect
          key={field.id}
          iconPath={field.iconPath}
          options={field.options}
          value={selections[field.id] ?? []}
          onChange={(v) => setFieldValue(field.id, v)}
          placeholder={field.placeholder}
          isLoading={field.isLoading}
        />
      ))}
    </>
  )

  if (isCompact) {
    return (
      <div className='dash-filter-bar dash-filter-bar--compact' ref={compactRef}>
        <button
          type='button'
          className='dash-filter-bar__trigger'
          aria-expanded={panelOpen}
          aria-haspopup='dialog'
          onClick={() => setPanelOpen((o) => !o)}
        >
          <svg
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            aria-hidden='true'
          >
            <line x1='4' y1='7' x2='20' y2='7' />
            <line x1='7' y1='12' x2='17' y2='12' />
            <line x1='10' y1='17' x2='14' y2='17' />
          </svg>
          <span>Filters</span>
          {activeCount > 0 && <span className='dash-filter-bar__count'>{activeCount}</span>}
        </button>
        {panelOpen && (
          <div className='dash-filter-bar__panel' role='dialog' aria-label='Filters'>
            <DateRangeFields startDate={startDate} endDate={endDate} onChange={setDates} />
            {selects}
            <div className='dash-filter-bar__panel-actions'>
              <button type='button' className='dash-filter-bar__reset' onClick={handleReset}>
                Reset
              </button>
              <button
                className='dash-filter-bar__apply-btn'
                onClick={handleApply}
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Apply'}
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className='dash-filter-bar'>
      <DateRangePill
        startDate={startDate}
        endDate={endDate}
        onChange={setDates}
        onReset={() => setDates(defaultStartDate, defaultEndDate)}
        canReset={isDateRangeDirty}
      />
      {selects}
      <button className='dash-filter-bar__apply-btn' onClick={handleApply} disabled={isLoading}>
        {isLoading ? 'Loading...' : 'Apply'}
      </button>
    </div>
  )
}

export default DashboardFilterBar
